import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, } from "@angular/core";
import { ControlContainer, FormControl, FormGroup } from "@angular/forms";
import { SelectComponent } from "@tylertech/forge";
import { Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

import { CustomValidatorMessage, evaluate, LgdErrorStateMatcher, LgdFormControl, LgdFormControlSelectConfig, } from "../lgdCommon";

@Component({
    selector: "lgd-select",
    templateUrl: "./lgdSelect.tmpl.html",
    styleUrls: ["./lgdSelect.component.scss"],
    standalone: false
})
export class LgdSelectComponent<TOption = unknown, TValue = unknown>
    implements OnInit, AfterViewInit, OnDestroy
{
    private readonly destructor$ = new Subject<void>();
    @ViewChild("select") readonly selectElement: ElementRef<SelectComponent>;

    private _initialized: boolean;

    @Input() readonly controlName: string | number;
    @Input() readonly label: string;
    @Input() readonly hideLabel: boolean;
    @Input() readonly placeholder: string;
    @Input() readonly tooltip: string;
    @Input() readonly loadingSpinner: boolean;
    @Input("options") readonly optionSource: Observable<ReadonlyArray<TOption>>;
    /**
     * ! WARNING - This function CANNOT rely on `this` being the defining class.
     * @deprecated pass the function through the LgdFormControlConfig
     */
    @Input() readonly displayValueFn: (x: TOption) => string;

    readonly errorStateMatcher = new LgdErrorStateMatcher();

    constructor(
        private readonly controlContainer: ControlContainer,
        private readonly changeDetectorRef: ChangeDetectorRef)
    { }

    get initialized(): boolean
    {
        return this._initialized;
    }

    get formGroup(): FormGroup
    {
        return this.controlContainer.control as FormGroup;
    }

    get formControl(): FormControl<TOption>
    {
        if (!this.formGroup || (this.controlName !== undefined && this.controlName == null))
        {
            throw new Error("Cannot access either formGroup or controlName.");
        }

        const controlNameStr = typeof this.controlName === "string" ? this.controlName : `${this.controlName}`;
        return this.formGroup.get(controlNameStr) as FormControl<TOption>;
    }

    get lgdFormControl(): LgdFormControl<LgdFormControlSelectConfig<TOption, TValue>, TValue>
    {
        return LgdFormControl.isLgdFormControl<LgdFormControlSelectConfig<TOption, TValue>>(this.formControl)
            ? this.formControl
            : undefined;
    }

    private _options: ReadonlyArray<Readonly<TOption>>;
    get options(): ReadonlyArray<Readonly<TOption>>
    {
        return this._options;
    }

    hasCustomValidators: boolean;
    customErrors: ReadonlyArray<CustomValidatorMessage>;

    ngOnInit(): void
    {
        if (!this.optionSource)
        {
            throw new Error(`options field is required. controlName was: ${JSON.stringify(this.controlName)}`);
        }

        if (this.lgdFormControl)
        {
            this.hasCustomValidators = !!this.lgdFormControl.data.customValidators;

            this.customErrors = this.hasCustomValidators
                ? this.lgdFormControl.data.customValidators.reduce(
                    (l, x) => l.concat(x.getErrorMessage(this.label)),
                    []
                )
                : [];
        }

        this.optionSource.pipe(takeUntil(this.destructor$))
            .subscribe(_value =>
            {
                this._options = _value;

                //This sets the initial value to the same exact type as the options to
                //  better handle the strict comparison within forge-select
                if (this._options?.length > 0 && this.formControl.value)
                {
                    const selectedValue = this.getValue(this.formControl.value);
                    const selectedOption = this._options.find(
                        x => this.getValue(x) === selectedValue);
                    this.lgdFormControl.setValue(
                        this.getValue(selectedOption),
                        { emitEvent: false });
                }

                this.changeDetectorRef.markForCheck();
            });

        this.formControl.valueChanges
            .pipe(takeUntil(this.destructor$))
            .subscribe(_value =>
            {
                this.changeDetectorRef.markForCheck();
            });

        this._initialized = true;
    }

    ngAfterViewInit(): void
    {
        if (!this.selectElement)
        {
            console.warn(`LgdSelectComponent.ngAfterViewInit - ViewChild selectElement is not defined!`);
        }

        if (this.lgdFormControl)
        {
            this.lgdFormControl.onFocus.pipe(takeUntil(this.destructor$))
                .subscribe((shouldFocus: boolean) =>
                {
                    if (shouldFocus)
                    {
                        /**
                         * Calling in a setTimeout to avoid a bug in our current @angular version.
                         */
                        setTimeout(() =>
                        {
                            this.selectElement.nativeElement.focus();
                        });
                    }
                });
        }
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    getDisplayValueFn(): (x: TOption) => string
    {
        if (!!this.lgdFormControl && !!this.lgdFormControl.data.displayValueFn)
        {
            return this.lgdFormControl.data.displayValueFn;
        }

        return this.displayValueFn || ((x: TOption) => (x ? x.toString() : ""));
    }

    getDisplayValue(x: TOption): string
    {
        return this.getDisplayValueFn()(x);
    }

    getValue(x: TOption): TValue
    {
        return x ? this.getValueFn()(x) : undefined;
    }

    private getValueFn(): (x: TOption) => TValue
    {
        if (!!this.lgdFormControl && !!this.lgdFormControl.data.valueFn)
        {
            return this.lgdFormControl.data.valueFn;
        }
        return this.defaultToValueFn;
    }

    private defaultToValueFn(x: TOption): TValue
    {
        return x as unknown as TValue;
    }

    hasError(customError: CustomValidatorMessage): boolean
    {
        return !!this.formControl && this.formControl.hasError(customError.errorKey);
    }

    get labelText(): string
    {
        return !this.hideLabel ? this.label : "";
    }

    showErrorMessage(id: string): boolean
    {
        return this.formControl.touched && this.formControl.hasError(id);
    }

    get isInvalid(): boolean
    {
        return this.formControl.touched && this.formControl.invalid;
    }

    get isRequired(): boolean
    {
        return !!this.lgdFormControl && evaluate(this.lgdFormControl.data.required);
    }
}
